import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { useMemoizedFn } from 'ahooks';
import { PagingQuery } from 'tea-component';
import Filter from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Filter';
import Visualis from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Visualis';
import Tables from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Tables';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import useMetricsDateRange from '@intelligence/v3/_hooks/useMetricsDateRange';
import useDemographicsRegionsOptions from '@intelligence/hooks/useDemographicsRegionsOptions';
import useOverlap from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/_hooks/useOverlap';
import { getMobileOverlapAnlysisCountryFilters } from '@intelligence/services/filter';
import { buildDateDisabledCallbackMap } from '@intelligence/v3/utils/date';
import { getAllCountryCodesByRegions } from '@intelligence/v3/utils/region';
import { getDateRangeOfMetricsPage, getValidDateRangeOfMetricsPage } from '@intelligence/v3/utils/topcharts';
import { ChartType, SortType, SortMode } from '@intelligence/pages/TopCharts/_config/enums';
import { buriedPointGame } from '@home/utils/buriedPoint';
import { feachTimeTypeToFilterTimeType } from '@intelligence/utils/common';
import { dbiPageLogMes as PAGE_LOG_CFGS } from '@intelligence/utils/buriedPointCfg';
import {
  getInitialFetchParams,
  calcTopRanksMetrics,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/utils';
import { DATASOURCE_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics';
import { DEFAULT_FETCH_PARAMS } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/constants';
import {
  SORTABLE_METRICS,
  SORTABLE_METRICS_METADATAS_MAPPING,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/constants/metrics';

import type { IBaseProps, IGameType } from '@intelligence/types/common';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type {
  TableSelectStatusType,
  TableSelectionType,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import type {
  IMobileDetailsOverlapRank,
  IMobileDetailsOverlapFilterParams,
  IMobileDetailsOverlapRanksFetchParams,
  IMobileDetailsOverlapTrendsFetchParams,
  IMobileDetailsOverlapTopRanksMetrics,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/types';

const PAGE_LOG_KEY = 'mobileGameDetailsOverlap';
const PAGE_DOWNLOAD_EXCEL_KEY = 'mobileGameDetailsOverlapDownloadExcel';
const PAGE_KEY = 'details-mobile-overlap';
const PAGE_LOG_INFO = PAGE_LOG_CFGS?.[PAGE_LOG_KEY];

interface IMobileDetailsOverlapProps extends IBaseProps {
  loading: boolean;
  entityType: IGameType;
  setActiveId: React.Dispatch<React.SetStateAction<string>>;
  gameId: string;
  gameName: string;
}

const MobileDetailsOverlap: React.FC<IMobileDetailsOverlapProps> = (props) => {
  const { loading: loadingProps, entityType = 'mobile', setActiveId, gameId, gameName, style, className } = props;

  const defaultMetric = SORTABLE_METRICS?.[0] || '';
  const defaultMetricObj = SORTABLE_METRICS_METADATAS_MAPPING?.[defaultMetric] || {};

  const { formatMessage } = useIntl();

  const { loading: definitionsLoading, genreMap: genreOptions } = useQueryDefinition();

  const {
    marketLoading: regionsOptionsLoading,
    regionOptions: regionsOptions,
    allMarkets,
  } = useDemographicsRegionsOptions({
    appIds: [],
    getCountriesFilterFun: () => {
      return getMobileOverlapAnlysisCountryFilters({ game_ids: [gameId] });
    },
  });

  const loading = loadingProps || definitionsLoading || regionsOptionsLoading;

  const { isOperateDetail, detailFilterExtend } = useFilterExtend({});

  const {
    // 是否加载中
    loading: metricsGranularityDateRangesLoading,
    dateRangeMap: metricsDateRangeMap,
    // 指标的日期粒度与有数据日期范围的映射
    granularityDateRangeMap: metricsGranularityDateRangeMap,
  } = useMetricsDateRange(!loading && !regionsOptionsLoading, {
    game_ids: [gameId],
    app_ids: [],
    granularities: ['monthly'],
    markets: getAllCountryCodesByRegions(regionsOptions),
    metrics: [
      DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE,
      DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY,
    ],
    group_by: [],
  });

  const rangePickerDisabledMap = useMemo<IDateRangeDisabledCallbackDateTypeMap>(() => {
    if (metricsGranularityDateRangesLoading) {
      return {};
    }

    return buildDateDisabledCallbackMap(metricsGranularityDateRangeMap);
  }, [metricsGranularityDateRangesLoading, metricsGranularityDateRangeMap]);

  const {
    tableTotal,
    tableData,
    chartType,
    setChartType,
    isAggregate,
    setIsAggregate,
    barOrPieData,
    setBarOrPieData,
    lineData,
    fetchParams,
    getGameData,
    getLineChartData,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    // isNodata,
    handleClearSearch,
    transTableDataForSearch,
    fetchOverlapRanks,
    // reset,
  } = useOverlap({
    entityType,
    getInitialFetchParams: () =>
      getInitialFetchParams(isOperateDetail, detailFilterExtend, {
        ...DEFAULT_FETCH_PARAMS,
        game_ids: !gameId ? [] : [gameId],
        markets: !!allMarkets?.length && !allMarkets.includes('us') ? allMarkets.slice(0, 1) : ['us'],
      }),
    refreshListDataCallback: (list: IMobileDetailsOverlapRank[]) => {
      setTopRanksMetrics(calcTopRanksMetrics(list));
    },
  });

  // 各个指标，数值从高到低排名前10的游戏id集合
  const [topRanksMetrics, setTopRanksMetrics] = useState<IMobileDetailsOverlapTopRanksMetrics>({});

  const [sortByName, setSortByName] = useState(formatMessage({ id: defaultMetricObj?.name || '' }));
  const [sortByKey, setSortByKey] = useState(defaultMetric);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isError, setIsError] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [visualisLoading, setVisualisLoading] = useState(true);

  const getData = async (params: IMobileDetailsOverlapRanksFetchParams, isCheckGame: boolean, isSearch = false) => {
    try {
      const paramsTmp = {
        ...params,
        search_by_ids: isSearch ? params?.search_by_ids : [],
      };
      setTableLoading(true);
      if (!isCheckGame) {
        // 没有勾选时点排序则重新Loading柱状图数据
        setVisualisLoading(true);
      }
      await getGameData(paramsTmp, isCheckGame);
      setIsError(false);
    } catch (exception) {
      setIsError(true);
    } finally {
      setTableLoading(false);
      setVisualisLoading(false);
    }
  };

  const getTrend = (params: IMobileDetailsOverlapTrendsFetchParams) => {
    setVisualisLoading(true);
    getLineChartData(params)
      .then(() => {
        setVisualisLoading(false);
        setIsError(false);
      })
      .catch(() => {
        setVisualisLoading(false);
        setIsError(true);
      });
  };

  // 切换柱状图是否聚合
  const handleIsAggregateChange = (value: boolean) => {
    if (!value && tableSelection?.length && isNewComfirm && isNewCheckGame) {
      const idList = tableSelection?.map((t) => t?.unified_id) || [];
      getTrend({ ...fetchParams, cross_app_ids: idList });
    }
    setIsAggregate(value);
  };

  // 切换charts类型事件
  const handleChartType = (value: ChartType) => {
    if (value === ChartType.Line && tableSelection?.length && isNewComfirm && isNewCheckGame) {
      const idList = tableSelection?.map((t) => t?.unified_id);
      getTrend({ ...fetchParams, cross_app_ids: idList });
    }
    setChartType(value);
  };

  const handleTableSortChange = (e: SortByItemType) => {
    const fetchParamsTmp = {
      ...fetchParams,
      order_source: (e as any)?.orderSource || '',
      order_metric: e?.sortMetric || '',
      order: e?.sortVal as SortType,
      ratio_type: e?.ratioType || SortMode.Value,
      page: 1,
      // page_size: 100,
      page_size: 50,
    };
    setSortByName((e as any)?.title);
    setSortByKey(e?.sortMetric || '');
    getData(fetchParamsTmp, operatedGame);
  };

  const handleTablePagingChange = (e: PagingQuery) => {
    const fetchParamsTmp = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      // page_size: e?.pageSize || 100,
      page_size: e?.pageSize || 50,
    };
    getData(fetchParamsTmp, true);
  };

  // 表格列表项选择事件回调
  const handleTableCheckGame = (selectList: TableSelectionType[], checkStatus: TableSelectStatusType) => {
    setIsNewComfirm(true);
    setIsNewCheckGame(true);

    const idList = selectList?.map((t) => t?.unified_id) || [];
    if (chartType === ChartType.Line || (chartType === ChartType.Bar && !isAggregate)) {
      getTrend({ ...fetchParams, cross_app_ids: idList });
    }
    /** 是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
    }
    // @ts-ignore
    setBarOrPieData(selectList);
    setTableSelection(selectList);
  };

  const getTableSearchList = (id: string) => {
    getData({ ...fetchParams, search_by_ids: [id] }, true, true);
  };

  const handleConfirm = useMemoizedFn((payload: IMobileDetailsOverlapFilterParams) => {
    const fetchParamsTmp = {
      ...fetchParams,
      ...payload,
      page: 1,
      // page_size: 100,
      page_size: 50,
    };
    // 修改筛选器后，游戏应该重置为未勾选状态
    setOperatedGame(false);
    // 请求接口
    getData(fetchParamsTmp, false);

    setIsNewComfirm(true);
    setIsNewCheckGame(true);
  });

  const handleReset = useMemoizedFn(() => {
    setOperatedGame(false);
  });

  const handleInit = useMemoizedFn(() => {});

  const handlePageLog = () => {
    buriedPointGame(PAGE_LOG_INFO, gameId || '');
  };

  useEffect(() => {
    if (loading) return;
    if (metricsGranularityDateRangesLoading) return;

    const dateRangeOfMetricsPage = getDateRangeOfMetricsPage(
      'monthly',
      [DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE, DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY],
      metricsDateRangeMap,
    );
    const validDateRangeOfMetricsPage = getValidDateRangeOfMetricsPage(fetchParams as any, dateRangeOfMetricsPage);
    const marketsTmp = fetchParams?.markets?.filter((market) => allMarkets?.includes(market));
    const fetchParamsTmp = {
      ...fetchParams,
      ...validDateRangeOfMetricsPage,
      game_ids: !gameId ? [] : [gameId],
      markets: marketsTmp?.length ? marketsTmp : ['us'],
    };
    setTableLoading(true);
    setVisualisLoading(true);
    getData(fetchParamsTmp, operatedGame);
  }, [loading, metricsGranularityDateRangesLoading, metricsDateRangeMap, gameId]);

  useEffect(() => {
    handlePageLog();
  }, [gameId]);

  return (
    <div style={style} className={classnames('', className)}>
      {/* 筛选器 */}
      <Filter
        loading={definitionsLoading || metricsGranularityDateRangesLoading || regionsOptionsLoading}
        rangePickerDisabledMap={rangePickerDisabledMap}
        regionsOptions={regionsOptions}
        genreOptions={genreOptions}
        fetchParams={fetchParams}
        onConfirm={handleConfirm}
        onReset={handleReset}
        onInit={handleInit}
        entityType={entityType}
        buriedPointParams={PAGE_LOG_INFO}
      />
      {/* 可视化模块，包含指标切换Segment、排序信息展示、可视化类型切换 */}
      <Visualis
        tableLoading={tableLoading}
        visualisLoading={visualisLoading}
        chartType={chartType}
        handleChartType={handleChartType}
        isAggregate={isAggregate}
        handleIsAggregateChange={handleIsAggregateChange}
        topChartsType={PAGE_KEY}
        imageType={entityType}
        dateType={feachTimeTypeToFilterTimeType(fetchParams?.granularity || '')}
        tableData={barOrPieData}
        lineData={lineData}
        fetchParams={fetchParams}
        sortByName={sortByName}
        sortByKey={sortByKey}
      />
      {/* 宽表模块，包含Heat Map、宽表搜索、下载、宽表 */}
      <Tables
        entityType={entityType}
        isIEGG={fetchParams?.genre_source === 'iegg'}
        gameId={gameId}
        gameName={gameName}
        regionsOptions={regionsOptions}
        pageKey={PAGE_KEY}
        pageDownloadExcelKey={PAGE_DOWNLOAD_EXCEL_KEY}
        pageLogInfo={PAGE_LOG_INFO}
        setActiveId={setActiveId}
        tableLoading={tableLoading}
        tableTotal={tableTotal}
        tableSelection={tableSelection}
        tableSelectionAll={defaultSelectionGames}
        tableData={tableData}
        fetchParams={fetchParams}
        topRanksMetrics={topRanksMetrics}
        handleClearSearch={handleClearSearch}
        transTableDataForSearch={transTableDataForSearch}
        fetchOverlapRanks={fetchOverlapRanks}
        getTableSearchList={getTableSearchList}
        onTableSortChage={handleTableSortChange}
        onTablePagingChange={handleTablePagingChange}
        onTableCheckGame={handleTableCheckGame}
      />
    </div>
  );
};

export default React.memo(MobileDetailsOverlap);
